import { DatePipe, Location } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { PolicyService, User, UsersService, UtilsService } from '@horizon/core';
import { HeaderToolbarOption } from '@horizon/core-layouts';
import {
  DialogNotification,
  NotificationService,
  ToastNotificationItem,
  ToastService,
} from '@horizon/core-notifications';
import { ValidationRulesService } from '@horizon/dm-core';
import { TranslateService } from '@ngx-translate/core';
import { GenesysTelephonyService } from '@shared/components/horizon-genesys-telephony/services/genesys-telephony.service';
import { AppSectionsPermissionsKey } from '@shared/enums/permissions.enums';
import { MenuRoutePaths, RoutePaths } from '@shared/enums/routes.enums';
import { AccountDataService } from '@shared/services/account-data.service';
import { ApiExceptionsHandlerService } from '@shared/services/api-exceptions-handler.service';
import { EventFireService } from '@shared/services/event-fire.service';
import { HorizonGenesysCallLogService } from '@shared/services/horizon-genesys-call-log.service';
import { LoanEngineApiService } from '@shared/services/loan-engine-api.service';
import { MonitorAccessApiService } from '@shared/services/monitor-access-api.service';
import { StorageApiService } from '@shared/services/storage-api.service';
import { TemplatesDataService } from '@shared/services/templates-data.service';
import { CloseEventArgs } from '@syncfusion/ej2-angular-popups';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { globalSettings } from 'src/global-settings';
import { SearchApiService } from '../core/services/search.services';
import { HeaderConfigService } from './config/header-config.service';
import { layout } from './config/layout';
import { SideBarMenuConfigService } from './config/side-bar-menu-config.service';
import { AuthorizationService } from './services/authorization.service';

@Component({
  selector: 'core',
  templateUrl: './core.component.html',
  styleUrls: ['./core.component.scss'],
})
export class CoreComponent implements OnInit {
  private readonly destroy$ = new Subject<void>();

  @HostListener('window:beforeunload')
  onBeforeUnload() {
    //Call log tabs storage clean up
    this.storageApiService.deleteLocalStorageData('genesysData');
    this.storageApiService.deleteLocalStorageData('thirdPartyCall');
    this.storageApiService.deleteLocalStorageData('thirdPartyCalledData');
    this.storageApiService.deleteLocalStorageData('processed');
    this.storageApiService.deleteLocalStorageData('isViewCustomerOverview');
    this.storageApiService.deleteLocalStorageData('isCustomerOverview');
  }

  public loginShortDisplayName: string = undefined;
  public canUserSearch: boolean = true;
  public currentLanguage: string;
  public user: User;
  public userPhoto: SafeResourceUrl | null = null;

  public isIframe = window !== window.parent && !window.opener;
  public isLoginWanted: boolean = false;

  public newNotification: ToastNotificationItem;
  public notificationsDialog: DialogNotification;
  public showLoadingSpinner: boolean = false;
  public dataRecentsSearch: any;
  public globalSettings: any = globalSettings;
  //Menu permissions enums
  public menuRoutingPaths: any = MenuRoutePaths;
  public menuItemsPermissions: any = AppSectionsPermissionsKey;

  public showGenesysSubstituteModal: boolean = false;
  //Call log modal

  public callLogOrigin: 'Default' | 'CustomerOverview' | 'ViewCallLog' =
    'Default';
  public showCallLog = false;

  recentSearchContent = this.utils.getLayoutElementById(
    layout,
    'recent-search'
  );
  public templateRecentSearch: any = {
    id: 'template',
    fields: [
      {
        name: 'anchored',
        type: 'icon',
      },
      {
        name: 'account',
        type: 'url',
      },
      {
        name: 'text',
      },
      {
        name: 'date',
        type: 'date',
      },
    ],
  };

  public systemDate: any;

  public startGenesysTelephony: boolean = false;
  public showGenesysTelephony: boolean = false;
  public showIncomingCallNotification: boolean = false;
  showHeader: boolean = true;

  constructor(
    private readonly router: Router,
    private datePipe: DatePipe,
    public headerSettingsService: HeaderConfigService,
    private authService: MsalService,
    private authorizationService: AuthorizationService,
    private translate: TranslateService,
    private policyService: PolicyService,
    private usersService: UsersService,
    private domSanitizer: DomSanitizer,
    private eventFireService: EventFireService,
    public menuSettingsService: SideBarMenuConfigService,
    private readonly toastService: ToastService,
    private readonly notificationService: NotificationService,
    private host: ElementRef<HTMLElement>,
    private readonly utils: UtilsService,
    private searchApiService: SearchApiService,
    private location: Location,
    private templatesDataService: TemplatesDataService,
    private loanEngineApi: LoanEngineApiService,
    private apiExceptionsHandlerService: ApiExceptionsHandlerService,
    private genesysTelephonyService: GenesysTelephonyService,
    public accountDataService: AccountDataService,
    private dynamicValidationService: ValidationRulesService,
    private readonly callLogService: HorizonGenesysCallLogService,
    private zone: NgZone,
    private monitorAccessService: MonitorAccessApiService,
    private readonly storageApiService: StorageApiService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        if (event.url.includes('shortcuts')) {
          this.showHeader = false;
        }
      });
    this.menuSettingsService.setMenuColors(this.host);

    this.notificationService.getLoadingSpinner().subscribe((loading) => {
      this.showLoadingSpinner = loading;
    });

    this.toastService.getToastNotification().subscribe((notification) => {
      this.newNotification = notification;
    });

    this.genesysTelephonyService
      .getGenesysTelephony()
      .subscribe((genesysTelephony) => {
        this.showGenesysTelephony = genesysTelephony;
      });

    this.genesysTelephonyService
      .getIncomingCallNotification()
      .subscribe((incomingCallNotification) => {
        this.showIncomingCallNotification = incomingCallNotification;
      });

    this.notificationService
      .getNotificationDialog()
      .subscribe((notifications) => {
        this.notificationsDialog = notifications;
      });
  }

  ngOnInit(): void {
    this.currentLanguage = this.translate.currentLang;
    this.setLoginDisplay();
    this.getActiveUsers();
    this.getUsers();
    this.monitorAccessService.initUsersDetails();
    this.getSystemDate();

    this.genesysTelephonyService.setGenesysTelephonyIcon('default');
    this.callLogService.setGenesysSubstituteIcon('default');
    this.checkRouteAndSetHeaderVisibility();
    this.checkGenesysGeneratedTabs();

    this.getTranslations('en');
    this.getTranslations('es');
  }

  private checkGenesysGeneratedTabs(): void {
    if (
      this.isGenesysDataUnpopulated() ||
      this.isTabAlreadyProcessed() ||
      this.isViewCustomerOverview()
    ) {
      return;
    }
    this.callLogOrigin = this.storageApiService.getLocalStorageData(
      'isCustomerOverview'
    )
      ? 'CustomerOverview'
      : 'Default';
    const isGeneratedTab = this.isGeneratedTab();
    this.markTabProcessingStatus(isGeneratedTab);
  }

  closeCallLog(args: CloseEventArgs): void {
    this.showCallLog = false;
    window.close();
  }

  private isTabAlreadyProcessed(): boolean | string {
    const isProcessed = this.storageApiService.getLocalStorageData('processed');
    return isProcessed;
  }

  private isGenesysDataUnpopulated() {
    const isUnpopulated =
      !this.storageApiService.getLocalStorageData('genesysData');
    return isUnpopulated;
  }

  private isViewCustomerOverview() {
    const isViewCustomer = this.storageApiService.getLocalStorageData(
      'isViewCustomerOverview'
    );
    return isViewCustomer;
  }

  private isGeneratedTab(): boolean {
    const generatedTabs =
      this.storageApiService.getLocalStorageData('generatedTabsOpen') || '{}';
    return Object.keys(generatedTabs).some((id) => generatedTabs[id]);
  }

  private markTabProcessingStatus(isGenerated: boolean): void {
    if (isGenerated) {
      this.storageApiService.setLocalStorageData('processed', 'true');
      this.showCallLog = true;
    }
  }

  public languageChanged(language: string): void {
    this.changeLanguage(language);
  }

  private checkRouteAndSetHeaderVisibility(): void {
    if (this.router.url.includes('shortcuts')) {
      this.showHeader = false;
    }
  }

  private changeLanguage(language: string): void {
    this.eventFireService.currentLanguageChanged(language);
    this.currentLanguage = language;
  }

  public goHome(): void {
    this.router.navigate([RoutePaths.HOME]);
  }

  public logout(): void {
    this.authorizationService.logout();
  }

  private setLoginDisplay(): void {
    const allAccounts = this.authService?.instance?.getAllAccounts();
    const localAccountId = allAccounts[0]?.localAccountId;

    this.fetchUser(localAccountId);
  }

  private fetchUser(userId): void {
    this.policyService.getUserInfo().subscribe({
      next: (res) => {
        this.user = res;
        localStorage.setItem(
          'currentUserPermissions',
          JSON.stringify(this.user?.['permissions'])
        );
        localStorage.setItem('roleGuid', this.user.roleGuid);
        this.changeLanguage(this.user.language ?? 'en');

        if (res?.userPhoto) {
          this.userPhoto = this.domSanitizer.bypassSecurityTrustResourceUrl(
            'data:image;base64,' + res.userPhoto
          );
        } else {
          this.user.userPhoto = null;
          const allAccounts = this.authService?.instance?.getAllAccounts();
          const loginDisplay = allAccounts?.length > 0;

          if (loginDisplay && allAccounts[0]?.name) {
            const splittedName = allAccounts[0]?.name.split(' ');
            this.loginShortDisplayName =
              splittedName[0][0] + splittedName[1][0];
            this.usersService.getAllScopes();
          }
        }

        this.policyService.getAllRoles().subscribe({
          next: (data) => {
            data.map((role) => {
              if (role.roleName === 'SuperUser') {
                this.user.roleGuid === role.groupId
                  ? localStorage.setItem('isSuperUser', 'true')
                  : localStorage.setItem('isSuperUser', 'false');
              }
            });
          },
          error: (errorResponse) => {
            this.apiExceptionsHandlerService.defaultErrorToasts(
              errorResponse,
              'policy-service'
            );
          },
        });
      },
      error: (errorResponse) => {
        this.apiExceptionsHandlerService.defaultErrorToasts(
          errorResponse,
          'policy-service'
        );
      },
    });
  }

  public confirmModal(args: any): void {
    this.notificationService.setExtraAction(args);
    this.notificationService.confirmActionClicked();
  }

  public cancelModal(): void {
    this.notificationService.cancelActionClicked();
  }

  public async totalData(): Promise<void> {
    const response = await this.searchApiService
      .getAccountsSearch()
      .pipe(take(1))
      .toPromise();
    response.map((item) => {
      item['lastSearchDate'] = this.datePipe.transform(
        item.lastSearchDate + 'Z',
        'dd/MM/yyyy HH:mm'
      );
    });
    this.dataRecentsSearch = response;
  }

  public updateData(args) {
    args.pin = !args.pin;
    this.searchApiService
      .postAccountSearch(args)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (response) => {
        await this.totalData();
      });
  }

  public selectItemClick(accountNumber): void {
    const found = this.dataRecentsSearch.find(
      (_) => _.accountNumber === accountNumber
    );
    if (found) {
      this.searchApiService
        .postAccountSearch(found)
        .pipe(takeUntil(this.destroy$))
        .subscribe(async (response) => {
          await this.totalData();
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router
                .navigate([RoutePaths.ACCOUNT, found.accountId])
                .then(() => {
                  this.templatesDataService.setTemplates({});
                  this.templatesDataService.setTemplatesReferences({});
                  this.templatesDataService.setItemsByTemplates({});
                });
            });
        });
    }
  }

  public onSearch(event: any): void {
    if (event.type === 'advanced') {
      this.router.navigate([RoutePaths.SEARCH]);
    } else if (isNaN(event.value) || event.value >= 0) {
      this.router.navigate([RoutePaths.SEARCH, event.value.trim()]);
    }
  }

  public onToolbarItemClicked(event: HeaderToolbarOption): void {
    switch (event.action) {
      case 'window_open':
        this.onWindowOpenAction(event);
        break;
      case 'genesys_telephony':
        this.startGenesysTelephony = true;
        this.onGenesysTelephonyAction(this.showGenesysTelephony, event);
        break;
      case 'genesys_substitute':
        this.setGenesysSubstituteModal();
        break;
      default:
        break;
    }
  }

  public onWindowOpenAction(event: any): void {
    window.open(event.url, '_blank');
  }

  public onGenesysTelephonyAction(
    showGenesysTelephony: boolean,
    event: any
  ): void {
    this.isLoginWanted = true;
    if (!showGenesysTelephony) {
      this.genesysTelephonyService.showGenesysTelephony();
      this.genesysTelephonyService.setGenesysTelephonyIcon('selected');
    } else {
      this.isLoginWanted = false;
      this.genesysTelephonyService.hideGenesysTelephony();
      this.genesysTelephonyService.setGenesysTelephonyIcon('default');
    }
  }

  public genesysLogout(): void {
    this.startGenesysTelephony = false;
    this.showGenesysTelephony = false;
    this.genesysTelephonyService.setGenesysTelephonyIcon('default');
  }

  private async getSystemDate(): Promise<void> {
    await this.loanEngineApi.getSystemDate().then(async (response: any) => {
      this.systemDate = response;
      this.accountDataService.setSystemDate(this.systemDate);
      this.dynamicValidationService.setSystemDate(this.systemDate);
    });
  }

  private setGenesysSubstituteModal(): void {
    this.callLogService.setGenesysSubstituteIcon('selected');
    this.showGenesysSubstituteModal = true;
  }

  public closeGenesysSubstituteModal(): void {
    this.callLogService.setGenesysSubstituteIcon('default');
    this.showGenesysSubstituteModal = false;
  }

  private async getTranslations(language: string): Promise<void> {
    await this.loanEngineApi
      .getTranslations(language)
      .then((response: any) => {
        localStorage.setItem(
          'loan-engine-' + language + '-translations',
          JSON.stringify(response)
        );
      })
      .catch((errorResponse) => {});
  }

  public getUsers(): void {
    this.policyService
      .getAllUsersSimplified()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.monitorAccessService.setAllUsers(response);
      });
  }

  public getActiveUsers(): void {
    this.policyService
      .getActiveUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.monitorAccessService.setActiveUsers(response);
      });
  }
}
